import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageContent } from '@/components/PageContent';
import { Tabs, TabList, Tab, TabPanels, TabPanel, TabIndicator, useToast } from '@chakra-ui/react';
import { usePostChallengeMutation } from '@/services/queries/challenge';
import { breadcrumb } from './breadcrumb';
import { modalFormSchema, type ModalFormData } from './schemas';
import { Step1Fields } from './components/Step1Fields';
import { Step2Fields } from './components/Step2Fields';
import { Step3Fields } from './components/Step3Fields';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { ChallengeStatus } from '@/model';

export const ApplicationChallengeCreate = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const toast = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const postMutation = usePostChallengeMutation();

  const form = useForm<ModalFormData>({
    defaultValues: {
      step: 1,
      status: undefined,
      title: '',
      description: '',
      time_limit_seconds: undefined,
      has_start_date: false,
      starts_in: '',
      timeout_message: '',
      error_message: '',
      winner_message: '',
      communication_message: '',
      questions: [
        {
          uuid: crypto.randomUUID(),
          image: '',
          is_editing: true,
          title: '',
          answer: [
            { uuid: crypto.randomUUID(), content: '', is_correct: false },
            { uuid: crypto.randomUUID(), content: '', is_correct: false },
          ],
        },
      ],
    },
    resolver: zodResolver(modalFormSchema),
  });

  const onSubmit = (data: ModalFormData) => {
    const images: Record<string, File> = {};

    data.questions?.forEach(option => {
      if (option.image) {
        images[option.uuid] = option.image;
      }
    });

    const payload = {
      ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
      title: data.title,
      description: data.description,
      time_limit_seconds: data.time_limit_seconds,
      status: data.status,
      starts_in: data.starts_in,
      error_message: data.error_message,
      timeout_message: data.timeout_message,
      communication_message: data.communication_message,
      winner_message: data.winner_message,
      questions: data.questions.map(question => {
        return {
          uuid: question.uuid,
          title: question.title,
          answer: question.answer.map(answer => {
            return {
              uuid: answer.uuid,
              content: answer.content,
              is_correct: answer.is_correct,
            };
          }),
        };
      }),
      ...images,
    };

    postMutation.mutate(payload, {
      onSuccess: () => {
        toast({
          status: 'success',
          title: t('pages.challenges_details.toasts.create_success'),
        });

        navigate('/application/challenge');
      },
      onError: (resp: any) => {
        form.setValue('status', data.has_start_date ? ChallengeStatus.NOT_PUBLISHED : ChallengeStatus.PUBLISHED);

        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.challenges_details.toasts.create_error'),
        });
      },
    });
  };

  return (
    <PageContent title={t('pages.challenges_details.page_title')} breadcrumbItems={breadcrumb}>
      <Tabs
        variant='unstyled'
        borderTopWidth='8px'
        borderRadius='card'
        shadow='md'
        onChange={tabIndex => {
          setTabIndex(tabIndex);
          form.setValue('step', tabIndex + 1);
        }}
        index={tabIndex}
      >
        <TabList border={0}>
          <Tab>{t('pages.challenges_details.tabs.tab_1.title')}</Tab>
          <Tab>{t('pages.challenges_details.tabs.tab_2.title')}</Tab>
          <Tab>{t('pages.challenges_details.tabs.tab_3.title')}</Tab>
        </TabList>

        <TabIndicator mt='-48px' height='8px' bg='brand.500' borderTopLeftRadius={tabIndex === 0 ? '40px' : undefined} />

        <FormProvider {...form}>
          <TabPanels as='form' onSubmit={form.handleSubmit(onSubmit)} ref={formRef}>
            <TabPanel>
              <Step1Fields onStepSuccess={() => setTabIndex(1)} />
            </TabPanel>
            <TabPanel>
              <Step2Fields onStepSuccess={() => setTabIndex(2)} />
            </TabPanel>
            <TabPanel>
              <Step3Fields onStepSuccess={() => formRef.current?.requestSubmit()} />
            </TabPanel>
          </TabPanels>
        </FormProvider>
      </Tabs>

      <CenteredSpinner isLoading={postMutation.isLoading} />
    </PageContent>
  );
};
